import { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle2 } from "lucide-react";
import { useLanguage } from "@/components/sybrax/LanguageProvider";
import { content } from "@/components/sybrax/i18n";

const initialForm = { name: "", email: "", message: "" };

const Contact = () => {
    const { lang } = useLanguage();
    const c = content[lang].contact;

    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = c.errors.name;
        if (!form.email.trim()) {
            e.email = c.errors.emailRequired;
        } else if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
            e.email = c.errors.emailInvalid;
        }
        if (!form.message.trim() || form.message.trim().length < 10) {
            e.message = c.errors.message;
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onChange = (key) => (ev) => {
        setForm((f) => ({ ...f, [key]: ev.target.value }));
        if (errors[key]) setErrors((er) => ({ ...er, [key]: undefined }));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        if (!validate()) return;
        setLoading(true);
        setTimeout(() => {
            setLoading(false);
            setSubmitted(true);
            setForm(initialForm);
        }, 700);
    };

    const inputBase =
        "w-full rounded-xl bg-white dark:bg-white/[0.03] border border-slate-300 dark:border-white/10 px-4 py-3.5 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/40 focus:border-cyan-500 dark:focus:border-cyan-400/50 transition-all";

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative py-24 sm:py-32 overflow-hidden"
        >
            <div className="pointer-events-none absolute -top-32 right-0 w-[460px] h-[460px] rounded-full bg-cyan-500/[0.08] blur-[120px]" />
            <div className="pointer-events-none absolute bottom-0 left-0 w-[420px] h-[420px] rounded-full bg-blue-600/[0.06] blur-[120px]" />

            <div className="relative max-w-5xl mx-auto px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="text-center max-w-2xl mx-auto"
                >
                    <span className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.25em] text-cyan-600 dark:text-cyan-400">
                        <span className="mr-3 h-px w-8 bg-cyan-500/70 dark:bg-cyan-400/60" />
                        {c.label}
                        <span className="ml-3 h-px w-8 bg-cyan-500/70 dark:bg-cyan-400/60" />
                    </span>
                    <h2
                        data-testid="contact-title"
                        className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white"
                    >
                        {c.title}
                    </h2>
                    <p className="mt-5 text-base sm:text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
                        {c.intro}
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
                    className="mt-12 glass-card p-6 sm:p-10"
                >
                    {submitted ? (
                        <div
                            data-testid="contact-success"
                            className="flex flex-col items-center text-center py-10"
                        >
                            <div className="w-14 h-14 rounded-full bg-cyan-500/15 border border-cyan-500/40 dark:border-cyan-400/30 flex items-center justify-center shadow-[0_0_30px_rgba(6,182,212,0.35)]">
                                <CheckCircle2
                                    size={28}
                                    strokeWidth={1.6}
                                    className="text-cyan-600 dark:text-cyan-300"
                                />
                            </div>
                            <h3 className="mt-5 font-display text-2xl font-semibold text-slate-900 dark:text-white">
                                {c.successTitle}
                            </h3>
                            <p className="mt-2 text-slate-600 dark:text-slate-400 max-w-md">
                                {c.successMessage}
                            </p>
                            <button
                                type="button"
                                data-testid="contact-send-another"
                                onClick={() => setSubmitted(false)}
                                className="mt-7 inline-flex items-center justify-center rounded-full border border-slate-300 dark:border-white/15 bg-white dark:bg-white/[0.03] hover:bg-slate-50 dark:hover:bg-white/[0.07] hover:border-slate-400 dark:hover:border-white/25 text-slate-900 dark:text-white text-sm font-medium px-5 py-2.5 transition-all"
                            >
                                {c.sendAnother}
                            </button>
                        </div>
                    ) : (
                        <form
                            data-testid="contact-form"
                            onSubmit={onSubmit}
                            noValidate
                            className="grid grid-cols-1 sm:grid-cols-2 gap-5"
                        >
                            <div className="sm:col-span-2">
                                <label
                                    htmlFor="full-name"
                                    className="block text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 mb-2"
                                >
                                    {c.fullName}
                                </label>
                                <input
                                    id="full-name"
                                    type="text"
                                    data-testid="contact-input-name"
                                    value={form.name}
                                    onChange={onChange("name")}
                                    placeholder={c.namePlaceholder}
                                    className={inputBase}
                                    aria-invalid={!!errors.name}
                                />
                                {errors.name && (
                                    <p
                                        data-testid="contact-error-name"
                                        className="mt-1.5 text-xs text-rose-500 dark:text-rose-400"
                                    >
                                        {errors.name}
                                    </p>
                                )}
                            </div>

                            <div className="sm:col-span-2">
                                <label
                                    htmlFor="email"
                                    className="block text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 mb-2"
                                >
                                    {c.email}
                                </label>
                                <input
                                    id="email"
                                    type="email"
                                    data-testid="contact-input-email"
                                    value={form.email}
                                    onChange={onChange("email")}
                                    placeholder={c.emailPlaceholder}
                                    className={inputBase}
                                    aria-invalid={!!errors.email}
                                />
                                {errors.email && (
                                    <p
                                        data-testid="contact-error-email"
                                        className="mt-1.5 text-xs text-rose-500 dark:text-rose-400"
                                    >
                                        {errors.email}
                                    </p>
                                )}
                            </div>

                            <div className="sm:col-span-2">
                                <label
                                    htmlFor="message"
                                    className="block text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 mb-2"
                                >
                                    {c.message}
                                </label>
                                <textarea
                                    id="message"
                                    rows={5}
                                    data-testid="contact-input-message"
                                    value={form.message}
                                    onChange={onChange("message")}
                                    placeholder={c.messagePlaceholder}
                                    className={`${inputBase} resize-none`}
                                    aria-invalid={!!errors.message}
                                />
                                {errors.message && (
                                    <p
                                        data-testid="contact-error-message"
                                        className="mt-1.5 text-xs text-rose-500 dark:text-rose-400"
                                    >
                                        {errors.message}
                                    </p>
                                )}
                            </div>

                            <div className="sm:col-span-2 flex justify-end">
                                <button
                                    type="submit"
                                    data-testid="contact-submit"
                                    disabled={loading}
                                    className="group inline-flex items-center justify-center gap-2 rounded-full bg-cyan-500 hover:bg-cyan-400 text-[#030712] text-sm font-semibold px-7 py-3.5 transition-all hover:shadow-[0_0_30px_rgba(6,182,212,0.55)] disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {loading ? c.sending : c.submit}
                                    <Send size={15} className="transition-transform group-hover:translate-x-0.5" />
                                </button>
                            </div>
                        </form>
                    )}
                </motion.div>
            </div>
        </section>
    );
};

export default Contact;
